import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index.jsx";
import NotFound from "./pages/NotFound.jsx";
import SsoCallback from "./pages/SsoCallback.jsx";
import WidgetShell from "./components/widget/WidgetShell.jsx";
import { useMutation } from "@apollo/client/react";
import { VALIDATE_PARTNER_API_KEY } from "@/graphql/auth";
import { SplashScreen } from "./components/widget/SplashScreen.jsx";
import { setAuthToken, getAuthToken, clearAuthToken, setRefreshToken } from "./graphql/client.js";
import { useTranslation } from "./lib/i18n.jsx";
import { ConfirmDialogHost } from "./lib/confirmDialog.jsx";

const SSO_API_BASE = import.meta.env.VITE_API_BASE_URL || "https://api-p2p-stage.palzea.com";
const SSO_PARENT_ORIGIN = import.meta.env.VITE_PARENT_ORIGIN || "https://p2p-stage.palzea.com";
const SSO_DEFAULT_API_KEY = import.meta.env.VITE_DEFAULT_API_KEY || "";

const queryClient = new QueryClient();

// Security: Store validated API key in memory (not localStorage for security)
let validatedApiKey = null;

export const getValidatedApiKey = () => validatedApiKey;

const App = () => {
  // SSO path — bypass API key check
  // Matches /sso and the locale-prefixed /{locale}/p2p-widget/sso the widget is served under.
  const isSsoPath = /\/sso\/?$/.test(window.location.pathname);

  const [isAuthorized, setAuthorized] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidating, setIsValidating] = useState(!isSsoPath);

  const [validateApiKey, { loading }] = useMutation(VALIDATE_PARTNER_API_KEY);
  const { setLocale, t } = useTranslation();

  // Apply theme from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTheme = params.get("theme");

    if (urlTheme === "light") {
      document.documentElement.classList.remove("dark");
    } else {
      document.documentElement.classList.add("dark");
    }
  }, []);

  useEffect(() => {
    const handleThemeMessage = (event) => {
      // Reported finding: no origin check here, unlike the SSO_TOKEN handler
      // below. Can't pin this to one fixed origin like that handler does -
      // this widget is embedded on arbitrary partner sites by design, so
      // any of them legitimately needs to send this. event.source ===
      // window.parent at least restricts it to the actual embedding frame,
      // not any other window/frame on the page that happens to hold a
      // reference to this one.
      if (event.source !== window.parent) return;
      if (event.data?.type !== "THEME_CHANGE") {
        return;
      }

      const nextTheme = event.data.theme;

      if (nextTheme === "light") {
        document.documentElement.classList.remove("dark");
      } else {
        document.documentElement.classList.add("dark");
      }
    };

    window.addEventListener(
      "message",
      handleThemeMessage
    );

    return () => {
      window.removeEventListener(
        "message",
        handleThemeMessage
      );
    };
  }, []);

  // Mirrors the THEME_CHANGE listener above — lets an embedding page (e.g.
  // the merchant portal's live demo, or a partner's own switcher) update
  // the widget's language live via postMessage, without needing to reload
  // the iframe with a new ?locale= in its src.
  useEffect(() => {
    const handleLocaleMessage = (event) => {
      // Same reasoning as handleThemeMessage above.
      if (event.source !== window.parent) return;
      if (event.data?.type !== "LOCALE_CHANGE") return;
      if (event.data.locale) setLocale(event.data.locale);
    };

    window.addEventListener("message", handleLocaleMessage);
    return () => window.removeEventListener("message", handleLocaleMessage);
  }, [setLocale]);

  // Global postMessage SSO handler — works at any route, no iframe reload needed
  useEffect(() => {
    const onSsoMessage = async (event) => {
      if (event.origin !== SSO_PARENT_ORIGIN) return;
      if (event.data?.type !== "SSO_TOKEN" || !event.data?.token) return;

      try {
        const res = await fetch(`${SSO_API_BASE}/auth/sso`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: event.data.token }),
        });
        const data = await res.json();
        if (data?.access_token) {
          setAuthToken(data.access_token);
          if (data.refresh_token) setRefreshToken(data.refresh_token);
        } else {
          // Invalid/expired token — clear any existing session
          clearAuthToken();
        }
      } catch {
        clearAuthToken();
      }

      // Notify parent to navigate away from /sso (success or fail)
      window.parent.postMessage({ type: "SSO_COMPLETE" }, SSO_PARENT_ORIGIN);
    };

    window.addEventListener("message", onSsoMessage);
    // Notify parent that widget is ready to receive SSO_TOKEN
    window.parent.postMessage({ type: "WIDGET_READY" }, SSO_PARENT_ORIGIN);

    return () => window.removeEventListener("message", onSsoMessage);
  }, []);

  useEffect(() => {
    // SSO path — skip API key validation, render route directly
    if (isSsoPath) {
      setAuthorized(true);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const apiKey = params.get("apikey") || SSO_DEFAULT_API_KEY;

    if (!apiKey) {
      setErrorMessage("API Key missing. Please provide a valid API key to access the widget.");
      setIsValidating(false);
      return;
    }

    // Basic API key format validation before sending to server
    if (apiKey.length < 10 || apiKey.length > 256) {
      setErrorMessage("Invalid API key format.");
      setIsValidating(false);
      return;
    }

    // Validate API key with server
    validateApiKey({ variables: { apiKey } })
      .then(({ data }) => {
        const response = data?.validatePartnerApiKey;

        if (response?.success) {
          // Store validated API key in memory for subsequent requests
          validatedApiKey = apiKey;

          window.APP_CONFIG = {
            requireUserVideoKyc: response?.data?.requireUserVideoKyc || false,
            escrowTimeout: response?.data?.escrowTimeout || 60,
            adminPub: response?.data?.adminPub || "",
            isStagingPartner: response?.data?.mode === "STAGING",
          };

          setAuthorized(true);
        } else {
          validatedApiKey = null;
          setAuthorized(false);
          setErrorMessage(response?.message || "Invalid API key. Please contact support.");
        }
      })
      .catch((error) => {
        validatedApiKey = null;
        setAuthorized(false);
        // Don't expose internal error details to users
        setErrorMessage("Unable to verify API key. Please try again later.");
      })
      .finally(() => {
        setIsValidating(false);
      });
  }, [validateApiKey]);

  // Show loading state during validation
  if (loading || isValidating) {
    return (
      <div className="min-h-screen h-full flex flex-col items-center justify-center">
        <SplashScreen />
        {/* <div className="h-12 w-12 rounded-full border-4 border-white/20 border-t-primary animate-spin" />
          <p className="mt-4 text-sm text-muted-foreground">
            Checking authorization…
          </p> */}
      </div>
    );
  }

  // Show access denied if not authorized
  if (!isAuthorized) {
    return (
      <WidgetShell>
        <div className="h-full flex flex-col items-center justify-center text-center px-8">
          <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="mb-2 text-lg font-semibold text-primary">
            {t('app.access_denied', 'Access Denied')}
          </div>
          <p className="text-sm text-muted-foreground max-w-xs">
            {errorMessage}
          </p>
          <div className="mt-6 text-xs text-muted-foreground/60">
            {t('app.secured_by', 'Secured by')}
          </div>
          <div className="mt-1">
            <img src="/mainlogo.webp" alt="Palzea" className="h-10 w-auto hidden dark:block" />
            <img src="/mainlogoblack.webp" alt="Palzea" className="h-10 w-auto block dark:hidden" />
          </div>
        </div>
      </WidgetShell>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <ConfirmDialogHost />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/sso" element={<SsoCallback />} />
            {/* Locale-prefixed widget page the Laravel app serves (/{locale}/p2p-widget). */}
            <Route path="/:locale/p2p-widget" element={<Index />} />
            <Route path="/:locale/p2p-widget/sso" element={<SsoCallback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
